/**
 * You › Providers › Routing: the global default route for barks (agent, machine, provider account,
 * model). Everything defaults to Auto: Viko picks. A project's own default and a per-bark choice
 * in the bark box's route chip both override it. Stored as an encrypted setting.
 */
import type { BarkRouting } from '@viko/protocol'
import { Button, modelsFor } from '@viko/transcript-ui'
import { useEffect, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { cleanRouting, useBarkRoute } from '../lib/bark-route.js'
import { useMachines } from '../lib/queries.js'
import { errorText, Field, Select, useToast } from './ui.js'

export function BarkRouteDefault() {
  const b = useBackend()
  const toast = useToast()
  const route = useBarkRoute(undefined)
  const machines = useMachines().data ?? []
  const [v, setV] = useState<BarkRouting>({})
  useEffect(() => setV(cleanRouting(route.global)), [route.global])
  if (!b.entities) return null
  const accounts = machines
    .filter((m) => !v.machineId || m.id === v.machineId)
    .flatMap((m) =>
      (m.providerAccounts ?? [])
        .filter((a) => a.enabled && (!v.agent || a.provider === v.agent))
        .map((a) => ({
          key: `${m.id}\u0000${a.id}`,
          machineId: m.id,
          id: a.id,
          provider: a.provider,
          text: `${a.label} · ${m.name}`,
        })),
    )
  const set = (patch: Partial<BarkRouting>) => setV((cur) => cleanRouting({ ...cur, ...patch }))
  const save = async () => {
    try {
      await route.saveGlobal(v)
      toast('Default route saved')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <div className="grid gap-3 px-4" role="group" aria-label="Where new requests go">
      <p className="m-0 text-[14px] text-text-2">
        Where new requests go. Auto means Viko picks. A project's default or the route chip in the
        bark box overrides this.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Agent">
          <Select
            value={v.agent ?? ''}
            onChange={(e) => set({ agent: (e.target.value || undefined) as BarkRouting['agent'] })}
          >
            <option value="">Auto</option>
            <option value="claude">Claude</option>
            <option value="codex">Codex</option>
          </Select>
        </Field>
        <Field label="Machine">
          <Select
            value={v.machineId ?? ''}
            onChange={(e) => set({ machineId: e.target.value || undefined })}
          >
            <option value="">Auto</option>
            {machines.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.online ? 'online' : 'offline'})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Provider account">
          <Select
            value={v.accountId ? `${v.machineId}\u0000${v.accountId}` : ''}
            onChange={(e) => {
              const a = accounts.find((x) => x.key === e.target.value)
              set(
                a
                  ? { accountId: a.id, machineId: a.machineId, agent: a.provider }
                  : { accountId: undefined },
              )
            }}
          >
            <option value="">Auto</option>
            {accounts.map((a) => (
              <option key={a.key} value={a.key}>
                {a.text}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Model">
          <Select
            value={v.model ?? ''}
            disabled={v.agent === 'codex'}
            onChange={(e) => set({ model: e.target.value || undefined })}
          >
            <option value="">Auto</option>
            {modelsFor('claude').map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="primary" onClick={() => void save()}>
          Save default
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setV({})}>
          Reset to Auto
        </Button>
      </div>
    </div>
  )
}
